import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PiCaretDownBold, PiCaretUpBold } from 'react-icons/pi';
import toast from 'react-hot-toast';
import { Badge, Button } from '@v2/design-system';
import { cn } from '@v2/utils/cn';
import api from '@v2/lib/api';
import { canApproveCommission, isSuperAdmin } from '@v2/lib/auth';
import { useI18n } from '@v2/providers/I18nProvider';
import { CommissionApprovalModal, useCommissionActionPermissions } from '@v2/pages/commissioning/CommissionWalletPanel';
import SaleCancellationCard from '@v2/pages/commissioning/SaleCancellationCard';
import { invalidateDealCommissionQueries } from '@v2/features/ploomes-deals/detail/invalidateDealCommissionQueries';
import { COMMISSION_LIVE_QUERY } from '@v2/hooks/commissionLiveQuery';

type SellerInfo = {
    id: number;
    name: string;
    avatar_url: string | null;
};

type CascadeEntry = {
    beneficiary_id: number;
    beneficiary_name: string;
    beneficiary_avatar: string | null;
    commission_role: string;
    base_type: 'deal_amount' | 'seller_commission';
    rate_pp: number;
    amount: number;
};

type SplitAllocation = {
    user: SellerInfo;
    percent: number;
    rate_pp: number;
    amount: number;
    participant_role: string | null;
};

type CommissionSplit = {
    rule: string | null;
    rule_label: string | null;
    editable: boolean;
    pool_commission?: number;
    allocations: SplitAllocation[];
};

type SdrEntry = {
    user: SellerInfo | null;
    share_rate_pp: number;
    base_amount: number;
    amount: number;
    source: string | null;
};

type Installment = {
    id: number;
    installment_number: number;
    total_installments: number;
    installment_label: string;
    is_entry: boolean;
    amount: number;
    due_date: string | null;
    due_month_label: string | null;
    status: string;
    projected_status: string;
    status_label: string;
    is_considered_paid: boolean;
    commission_rate_pp: number;
    commission_allocation_percent: number | null;
    seller_commission: number;
    commission_cancelled?: boolean;
    commission_cancelled_amount?: number;
    commission_split?: CommissionSplit | null;
    sdr?: SdrEntry | null;
    cascade: CascadeEntry[];
};

type InstallmentsMeta = {
    payment_parcelas?: number | null;
    installments_total?: number;
    installments_remaining?: number;
    installments_considered_paid?: number;
    installments_defaulted?: number;
    installment_label?: string | null;
    commission_rate_pp?: number;
    commission_allocation_percent?: number | null;
    seller_commission_per_installment?: number;
    seller_commission_total?: number;
    seller_commission_received?: number;
    seller_commission_cancelled?: number;
    commission_forfeited_from?: string | null;
    seller_commission_pending?: number;
    seller_commission_defaulted?: number;
    carencia_months?: number | null;
    reason?: string;
    commission_available?: boolean;
    seller?: SellerInfo | null;
    commission_split?: Omit<CommissionSplit, 'pool_commission'> | null;
};

const fmt = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const fmtPct = (v: number) =>
    `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;

type Props = {
    dealId: number;
    /** Exibe cascata gerente/diretor (só financeiro/liderança). */
    showLeadership?: boolean;
};

function statusBadgeColor(projected: string, isPaid: boolean) {
    if (projected === 'paid' || (isPaid && projected === 'projected_paid')) {
        return 'success' as const;
    }
    if (projected === 'defaulted') {
        return 'danger' as const;
    }
    if (projected === 'projected_paid') {
        return 'info' as const;
    }
    if (projected === 'cancelled') {
        return 'danger' as const;
    }
    return 'warning' as const;
}

export default function DealInstallmentsPanel({ dealId, showLeadership = false }: Props) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const { canCancel, canUncancel, canCancelSale } = useCommissionActionPermissions();
    const [approval, setApproval] = useState<{ action: 'cancel' | 'uncancel'; installment: Installment } | null>(null);
    const canRecalc = canApproveCommission();
    const [editingDates, setEditingDates] = useState(false);
    const [draftDates, setDraftDates] = useState<Record<number, string>>({});
    const [dateReason, setDateReason] = useState('');

    const queryKey = ['commissioning', 'installments', dealId, showLeadership ? 'lead' : 'seller'] as const;

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey,
        queryFn: () => api.get(`/commissioning/deals/${dealId}/installments`).then((r) => r.data),
        retry: 1,
        ...COMMISSION_LIVE_QUERY,
    });

    const applyPayload = (res: { data?: Installment[]; meta?: InstallmentsMeta; message?: string }) => {
        qc.setQueryData(queryKey, { data: res?.data ?? [], meta: res?.meta ?? {} });
        void qc.invalidateQueries({ queryKey: ['commissioning', 'installments', dealId] });
        void qc.invalidateQueries({ queryKey: ['ploomes-deal', dealId, 'commission'] });
    };

    const recalcMutation = useMutation({
        mutationFn: () =>
            api.post(`/commissioning/deals/${dealId}/recalculate-installments`).then((r) => r.data),
        onSuccess: (res) => {
            toast.success(res?.message ?? t('v2.ploomes_deals.installments_recalc.toast_ok'));
            applyPayload(res);
            void invalidateDealCommissionQueries(qc, dealId);
        },
        onError: () => toast.error(t('v2.ploomes_deals.installments_recalc.toast_error')),
    });

    const datesMutation = useMutation({
        mutationFn: (payload: { items: { id: number; due_date: string }[]; reason?: string }) =>
            api.patch(`/commissioning/deals/${dealId}/installment-dates`, payload).then((r) => r.data),
        onSuccess: (res) => {
            toast.success(res?.message ?? t('v2.ploomes_deals.installments_dates.toast_ok', { count: res?.updated ?? 0 }));
            applyPayload(res);
            void invalidateDealCommissionQueries(qc, dealId);
            setEditingDates(false);
            setDateReason('');
        },
        onError: () => toast.error(t('v2.ploomes_deals.installments_dates.toast_error')),
    });

    const installments: Installment[] = (data?.data ?? []).map((row: Installment) => (
        showLeadership ? row : { ...row, cascade: [] }
    ));
    const meta: InstallmentsMeta = data?.meta ?? {};
    const metaSplit = meta.commission_split;
    const hasRateio =
        !!metaSplit &&
        metaSplit.rule !== 'legacy' &&
        metaSplit.rule !== 'DEFAULT' &&
        metaSplit.allocations.length > 1 &&
        (metaSplit.rule === 'REG-TA-01' ||
            metaSplit.rule === 'REG-SAD-01' ||
            metaSplit.allocations.some((a) => (a.percent ?? 0) < 100));

    const startEditDates = () => {
        const next: Record<number, string> = {};
        for (const row of installments) {
            next[row.id] = row.due_date ?? '';
        }
        setDraftDates(next);
        setEditingDates(true);
    };

    const saveDates = () => {
        const items = installments
            .map((row) => ({
                id: row.id,
                due_date: draftDates[row.id] ?? row.due_date ?? '',
            }))
            .filter((row) => row.due_date !== '' && row.due_date !== (installments.find((i) => i.id === row.id)?.due_date ?? ''));

        if (items.length === 0) {
            toast(t('v2.ploomes_deals.installments_dates.toast_noop'));
            setEditingDates(false);
            return;
        }

        datesMutation.mutate({
            items,
            reason: dateReason.trim() || undefined,
        });
    };

    if (isLoading) {
        return <DealInstallmentsSkeleton />;
    }

    if (isError) {
        return (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                <span>Erro ao carregar parcelas.</span>
                <button
                    onClick={() => refetch()}
                    className="underline hover:no-underline"
                >
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (installments.length === 0) {
        const message =
            meta.reason === 'no_payment_parcelas'
                ? 'Este deal não tem quantidade de parcelas cadastrada no Ploomes.'
                : meta.reason === 'no_amount'
                  ? 'Valor do deal indisponível para gerar parcelas.'
                  : 'Não foi possível montar o cronograma de parcelas para este deal.';

        return (
            <div className="space-y-3">
                <p className="text-sm text-gray-500 dark:text-zinc-400">{message}</p>
                {canRecalc && (
                    <Button
                        size="sm"
                        variant="outline"
                        isLoading={recalcMutation.isPending}
                        onClick={() => recalcMutation.mutate()}
                    >
                        {t('v2.ploomes_deals.installments_recalc.button')}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {canRecalc && (
                <div className="space-y-2 rounded-xl border border-indigo-200 bg-indigo-50/60 px-3 py-2 dark:border-indigo-800/50 dark:bg-indigo-950/30">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-indigo-900/80 dark:text-indigo-200/80">
                            {editingDates
                                ? t('v2.ploomes_deals.installments_dates.hint')
                                : t('v2.ploomes_deals.installments_recalc.hint')}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {!editingDates ? (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={startEditDates}
                                    >
                                        {t('v2.ploomes_deals.installments_dates.button')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        isLoading={recalcMutation.isPending}
                                        onClick={() => recalcMutation.mutate()}
                                    >
                                        {t('v2.ploomes_deals.installments_recalc.button')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={datesMutation.isPending}
                                        onClick={() => {
                                            setEditingDates(false);
                                            setDateReason('');
                                        }}
                                    >
                                        {t('v2.ploomes_deals.installments_dates.cancel')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        isLoading={datesMutation.isPending}
                                        onClick={saveDates}
                                    >
                                        {t('v2.ploomes_deals.installments_dates.save')}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                    {editingDates && (
                        <input
                            type="text"
                            value={dateReason}
                            onChange={(e) => setDateReason(e.target.value)}
                            placeholder={t('v2.ploomes_deals.installments_dates.reason_ph')}
                            className="w-full rounded-lg border border-indigo-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 dark:border-indigo-800 dark:bg-zinc-900 dark:text-zinc-100"
                        />
                    )}
                </div>
            )}

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryCard
                    label="Parcelas restantes"
                    value={String(meta.installments_remaining ?? 0)}
                    sub={`de ${meta.installments_total ?? installments.length} no total`}
                    accent="border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/40"
                />
                <SummaryCard
                    label="Comissão / parcela regular"
                    value={fmt(meta.seller_commission_per_installment ?? 0)}
                    sub={meta.commission_rate_pp != null ? `Taxa ${fmtPct(meta.commission_rate_pp)}` : undefined}
                    accent="border-emerald-200 bg-emerald-50 dark:border-emerald-800/50 dark:bg-emerald-950/40"
                />
                <SummaryCard
                    label="Já recebida (cronograma)"
                    value={fmt(meta.seller_commission_received ?? 0)}
                    sub={`${meta.installments_considered_paid ?? 0} pag(s) considerados pagos`}
                    accent="border-sky-200 bg-sky-50 dark:border-sky-800/50 dark:bg-sky-950/40"
                />
                <SummaryCard
                    label="A receber (em aberto)"
                    value={fmt(meta.seller_commission_pending ?? 0)}
                    sub="Parcelas ainda não pagas pelo cliente"
                    accent="border-orange-200 bg-orange-50 dark:border-orange-800/50 dark:bg-orange-950/40"
                />
            </div>

            {hasRateio && metaSplit && (
                <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-3 dark:border-sky-800/50 dark:bg-sky-950/30">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                            Rateio de comissão
                        </p>
                        {metaSplit.rule && (
                            <Badge color="info" variant="flat" rounded="pill" size="sm">
                                {metaSplit.rule}
                            </Badge>
                        )}
                    </div>
                    {metaSplit.rule_label && (
                        <p className="mb-2 text-xs text-sky-900/80 dark:text-sky-200/80">{metaSplit.rule_label}</p>
                    )}
                    <ul className="space-y-1.5">
                        {metaSplit.allocations.map((row) => (
                            <li
                                key={`${row.user.id}-${row.participant_role ?? 'p'}`}
                                className="flex items-center justify-between gap-2 rounded-lg bg-white/80 px-2.5 py-2 text-xs dark:bg-zinc-900/60"
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    {row.user.avatar_url ? (
                                        <img
                                            src={row.user.avatar_url}
                                            alt={row.user.name}
                                            className="h-6 w-6 shrink-0 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-[10px] font-bold text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
                                            {row.user.name.charAt(0)}
                                        </div>
                                    )}
                                    <span className="truncate font-semibold text-gray-800 dark:text-zinc-100">
                                        {row.user.name}
                                    </span>
                                </div>
                                <div className="shrink-0 text-right">
                                    <p className="font-bold text-sky-700 dark:text-sky-300">{fmtPct(row.percent)}</p>
                                    <p className="text-[10px] text-gray-500 dark:text-zinc-400">{fmtPct(row.rate_pp)} da venda</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Cronograma de parcelas</h4>
                    <p className="text-[11px] text-gray-500 dark:text-zinc-400">
                        Passado e mês atual = pago por padrão · futuro = pendente
                        {meta.carencia_months ? ` · carência ${meta.carencia_months} mês(es)` : ''}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    {meta.installment_label && (
                        <Badge color="info" variant="flat" rounded="pill" size="sm">
                            Pagos: {meta.installment_label}
                        </Badge>
                    )}
                    {(meta.installments_defaulted ?? 0) > 0 && (
                        <Badge color="danger" variant="flat" rounded="pill" size="sm">
                            Inadimplentes: {meta.installments_defaulted}
                        </Badge>
                    )}
                </div>
            </div>

            {(meta.installments_defaulted ?? 0) > 0 && (
                <p className="text-[11px] font-medium text-rose-600 dark:text-rose-400">
                    Comissão em inadimplência: {fmt(meta.seller_commission_defaulted ?? 0)}
                </p>
            )}

            {meta.commission_forfeited_from && (
                <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-200">
                    {t('v2.commissioning.history_month.deal_forfeited_banner', { month: meta.commission_forfeited_from })}
                </div>
            )}

            {(meta.seller_commission_cancelled ?? 0) > 0 && (
                <p className="text-[11px] font-medium text-violet-700 dark:text-violet-300">
                    {t('v2.commissioning.history_month.subtotal_forfeited')}: {fmt(meta.seller_commission_cancelled ?? 0)}
                </p>
            )}

            <div className="space-y-2">
                {installments.map((i) => (
                    <InstallmentRow
                        key={i.id}
                        canCancel={canCancel}
                        canUncancel={canUncancel}
                        onCancel={(row) => setApproval({ action: 'cancel', installment: row })}
                        onUncancel={(row) => setApproval({ action: 'uncancel', installment: row })}
                        installment={i}
                        seller={meta.seller}
                        editingDate={editingDates}
                        draftDate={draftDates[i.id] ?? i.due_date ?? ''}
                        onDraftDateChange={(value) =>
                            setDraftDates((prev) => ({ ...prev, [i.id]: value }))
                        }
                    />
                ))}
            </div>

            <p className="text-[11px] text-gray-500 dark:text-zinc-400">
                Comissão por parcela = valor da parcela × taxa efetiva do vendedor
                {meta.commission_allocation_percent != null && meta.commission_allocation_percent < 100
                    ? ` (${fmtPct(meta.commission_allocation_percent)} do rateio)`
                    : ''}
            </p>
            {canCancelSale && !isSuperAdmin() && <SaleCancellationCard dealId={dealId} />}
            <CommissionApprovalModal
                open={approval != null}
                action={approval?.action ?? 'cancel'}
                installmentId={approval?.installment.id}
                title={approval?.action === 'uncancel'
                    ? t('v2.commissioning.history_month.uncancel_commission')
                    : t('v2.commissioning.history_month.cancel_commission')}
                onClose={() => setApproval(null)}
                onConfirmed={async (code, reason) => {
                    const id = approval?.installment.id;
                    if (!id || !approval) return;
                    const path = approval.action === 'uncancel' ? 'uncancel-commission' : 'cancel-commission';
                    const res = await api.post(`/commissioning/installments/${id}/${path}`, {
                        reason,
                        approval_code: code,
                    });
                    toast.success(res.data?.message ?? t('v2.commissioning.cancelled.done'));
                    setApproval(null);
                    await refetch();
                    void invalidateDealCommissionQueries(qc, dealId);
                }}
            />
        </div>
    );
}

function DealInstallmentsSkeleton() {
    return (
        <div className="animate-pulse space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/60">
                        <div className="h-3 w-24 rounded bg-gray-200 dark:bg-zinc-700" />
                        <div className="mt-2 h-7 w-28 rounded bg-gray-200 dark:bg-zinc-700" />
                        <div className="mt-2 h-3 w-20 rounded bg-gray-200 dark:bg-zinc-700" />
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="space-y-2">
                    <div className="h-4 w-40 rounded bg-gray-200 dark:bg-zinc-700" />
                    <div className="h-3 w-64 rounded bg-gray-200 dark:bg-zinc-700" />
                </div>
                <div className="h-6 w-20 rounded-full bg-gray-200 dark:bg-zinc-700" />
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-zinc-700">
                <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800">
                    <div className="h-3 w-72 rounded bg-gray-200 dark:bg-zinc-700" />
                </div>
                <div className="space-y-2 p-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="grid grid-cols-7 gap-3">
                            <div className="h-4 rounded bg-gray-200 dark:bg-zinc-700" />
                            <div className="h-4 rounded bg-gray-200 dark:bg-zinc-700" />
                            <div className="h-4 rounded bg-gray-200 dark:bg-zinc-700" />
                            <div className="h-4 rounded bg-gray-200 dark:bg-zinc-700" />
                            <div className="h-4 rounded bg-gray-200 dark:bg-zinc-700" />
                            <div className="h-4 rounded bg-gray-200 dark:bg-zinc-700" />
                            <div className="h-4 rounded bg-gray-200 dark:bg-zinc-700" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function InstallmentRow({
    installment: i,
    seller,
    editingDate = false,
    draftDate,
    onDraftDateChange,
    onCancel,
    onUncancel,
    canCancel,
    canUncancel,
}: {
    installment: Installment;
    seller?: SellerInfo | null;
    editingDate?: boolean;
    draftDate?: string;
    onDraftDateChange?: (value: string) => void;
    onCancel?: (installment: Installment) => void;
    onUncancel?: (installment: Installment) => void;
    canCancel?: boolean;
    canUncancel?: boolean;
}) {
    const { t } = useI18n();
    const splitAllocations = i.commission_split?.allocations ?? [];
    const hasSplit = splitAllocations.length > 1;
    const hasSdr = !!i.sdr && (i.sdr.amount ?? 0) > 0 && !!i.sdr.user;
    const hasCascade = i.cascade && i.cascade.length > 0;
    const hasDetails = hasSplit || hasSdr || hasCascade;
    const poolCommission = i.commission_split?.pool_commission ?? i.seller_commission;
    const [open, setOpen] = useState(false);

    const ROLE_LABEL: Record<string, string> = {
        gerente: 'Gerente',
        diretor: 'Diretor',
        socio_diretor: 'Sócio-Dir.',
        originador: 'Originador',
        apoiador: 'Apoiador',
        participante: 'Participante',
        responsavel: 'Responsável',
        bonus_owner: 'Bônus 1,5%',
    };

    const taxaLabel = hasSplit && i.commission_split?.rule_label
        ? `${fmtPct(i.commission_rate_pp)} · SAD/TA`
        : fmtPct(i.commission_rate_pp);

    return (
        <div
            className={cn(
                'rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-zinc-700 dark:bg-zinc-900',
                i.commission_cancelled && 'border-violet-300 bg-violet-50/70 dark:border-violet-800/50 dark:bg-violet-950/25',
                !i.commission_cancelled && i.projected_status === 'defaulted' && 'border-rose-300 bg-rose-50/40 dark:border-rose-800/40 dark:bg-rose-950/20',
                i.is_entry && 'border-amber-200 bg-amber-50/40 dark:border-amber-800/40 dark:bg-amber-950/20',
                !i.is_entry && i.is_considered_paid && 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-800/40 dark:bg-emerald-950/20',
                hasDetails && 'cursor-pointer transition-colors hover:border-indigo-300 hover:bg-indigo-50/30 dark:hover:border-indigo-500/40 dark:hover:bg-indigo-950/20',
            )}
            onClick={() => hasDetails && setOpen((v) => !v)}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        {hasDetails ? (
                            open
                                ? <PiCaretUpBold className="h-3 w-3 shrink-0 text-indigo-400 dark:text-indigo-300" />
                                : <PiCaretDownBold className="h-3 w-3 shrink-0 text-gray-400 dark:text-zinc-500" />
                        ) : null}
                        {i.is_entry ? (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                                Entrada
                            </span>
                        ) : (
                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{i.installment_label}</p>
                        )}
                        {i.commission_cancelled && (
                            <span className="rounded-full bg-violet-700 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                                {t('v2.commissioning.history_month.forfeited_badge')}
                            </span>
                        )}
                        {hasSplit && i.commission_split?.rule && (
                            <Badge color="info" variant="flat" rounded="pill" size="sm">
                                {i.commission_split.rule_label ?? i.commission_split.rule}
                            </Badge>
                        )}
                    </div>
                    {editingDate ? (
                        <div
                            className="mt-1 flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <span className="text-xs text-zinc-600 dark:text-zinc-400">Venc.:</span>
                            <input
                                type="date"
                                value={draftDate ?? ''}
                                onChange={(e) => onDraftDateChange?.(e.target.value)}
                                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
                            />
                        </div>
                    ) : (
                        <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">Venc.: {i.due_month_label ?? i.due_date ?? '—'}</p>
                    )}
                    {!open && hasSplit && (
                        <p className="mt-1 text-[11px] text-sky-700 dark:text-sky-300">
                            {splitAllocations.map((row) => row.user.name.split(' ').slice(0, 2).join(' ')).join(' · ')}
                        </p>
                    )}
                </div>
                <Badge
                    color={statusBadgeColor(i.projected_status, i.is_considered_paid)}
                    variant="flat"
                    rounded="pill"
                    size="sm"
                >
                    {i.status_label}
                </Badge>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Metric label="Valor" value={fmt(i.amount)} />
                <Metric label="Taxa" value={taxaLabel} />
                <Metric
                    label={hasSplit ? 'Comissão (pool)' : 'Comissão'}
                    value={i.commission_cancelled ? fmt(0) : fmt(poolCommission)}
                    valueClass={i.commission_cancelled ? 'text-violet-700 dark:text-violet-300' : 'text-emerald-700 dark:text-emerald-400'}
                />
            </div>
            {i.commission_cancelled && (i.commission_cancelled_amount ?? 0) > 0 && (
                <p className="mt-2 text-[11px] font-medium text-violet-700 line-through dark:text-violet-300">
                    {t('v2.commissioning.history_month.forfeited_badge')}: {fmt(i.commission_cancelled_amount ?? 0)}
                </p>
            )}
            {(canCancel && !i.commission_cancelled && (i.projected_status === 'defaulted' || i.status === 'defaulted')) || (canUncancel && i.commission_cancelled) ? (
                <div className="mt-2 flex justify-end" onClick={(e) => e.stopPropagation()}>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => (i.commission_cancelled ? onUncancel?.(i) : onCancel?.(i))}
                    >
                        {i.commission_cancelled
                            ? t('v2.commissioning.history_month.uncancel_commission')
                            : t('v2.commissioning.history_month.cancel_commission')}
                    </Button>
                </div>
            ) : null}
            {!hasSplit && (i.seller_commission ?? 0) > 0 && (
                <p className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-400">
                    Comissão do vendedor nesta parcela: {fmt(i.seller_commission)}
                </p>
            )}
            {hasSplit && (i.seller_commission ?? 0) > 0 && (
                <p className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-400">
                    Sua fatia nesta parcela: {fmt(i.seller_commission)}
                </p>
            )}

            {open && hasDetails && (
                <div className="mt-3 space-y-1.5 border-t border-indigo-100 pt-3 dark:border-indigo-900/40">
                    {hasSplit ? (
                        <>
                            <p className="px-1 text-[10px] font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-400">
                                Rateio
                                {i.commission_split?.rule_label ? ` · ${i.commission_split.rule_label}` : ''}
                            </p>
                            {splitAllocations.map((row) => (
                                <ParticipantRow
                                    key={`${row.user.id}-${row.participant_role ?? 'p'}`}
                                    tone="sky"
                                    name={row.user.name.split(' ').slice(0, 2).join(' ')}
                                    avatarUrl={row.user.avatar_url}
                                    subtitle={[
                                        row.participant_role ? (ROLE_LABEL[row.participant_role] ?? row.participant_role) : null,
                                        `${fmtPct(row.percent)}`,
                                        `${fmtPct(row.rate_pp)} da venda`,
                                    ].filter(Boolean).join(' · ')}
                                    amount={fmt(row.amount)}
                                />
                            ))}
                        </>
                    ) : (
                        <ParticipantRow
                            tone="emerald"
                            name={seller?.name?.split(' ').slice(0, 2).join(' ') ?? 'Vendedor'}
                            avatarUrl={seller?.avatar_url}
                            subtitle={`${fmtPct(i.commission_rate_pp)} da venda`}
                            amount={fmt(i.seller_commission)}
                        />
                    )}

                    {hasSdr && i.sdr?.user && (
                        <>
                            <p className="px-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-teal-600 dark:text-teal-400">
                                SDR · {fmtPct(i.sdr.share_rate_pp)} da comissão do owner
                            </p>
                            <ParticipantRow
                                tone="teal"
                                name={i.sdr.user.name.split(' ').slice(0, 2).join(' ')}
                                avatarUrl={i.sdr.user.avatar_url}
                                subtitle={`${fmtPct(i.sdr.share_rate_pp)} sobre ${fmt(i.sdr.base_amount)}`}
                                amount={fmt(i.sdr.amount)}
                            />
                        </>
                    )}

                    {hasCascade && (
                        <>
                            <p className="px-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-500 dark:text-indigo-400">
                                Liderança
                            </p>
                            {i.cascade.map((c) => (
                                <ParticipantRow
                                    key={c.beneficiary_id}
                                    tone="indigo"
                                    name={c.beneficiary_name.split(' ').slice(0, 2).join(' ')}
                                    avatarUrl={c.beneficiary_avatar}
                                    subtitle={`${ROLE_LABEL[c.commission_role] ?? c.commission_role} · ${c.rate_pp}% ${c.base_type === 'seller_commission' ? 'da comissão' : 'da venda'}`}
                                    amount={fmt(c.amount)}
                                />
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

const PARTICIPANT_TONE = {
    emerald: {
        shell: 'border-emerald-200 dark:border-emerald-800/50',
        ring: 'ring-emerald-100 dark:ring-emerald-800/50',
        avatar: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
        subtitle: 'text-emerald-600 dark:text-emerald-400',
        amount: 'text-emerald-700 dark:text-emerald-300',
    },
    teal: {
        shell: 'border-teal-200 dark:border-teal-800/50',
        ring: 'ring-teal-100 dark:ring-teal-800/50',
        avatar: 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
        subtitle: 'text-teal-600 dark:text-teal-400',
        amount: 'text-teal-700 dark:text-teal-300',
    },
    indigo: {
        shell: 'border-indigo-200 dark:border-indigo-800/50',
        ring: 'ring-indigo-100 dark:ring-indigo-800/50',
        avatar: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
        subtitle: 'text-indigo-500 dark:text-indigo-400',
        amount: 'text-indigo-600 dark:text-indigo-300',
    },
    sky: {
        shell: 'border-sky-200 dark:border-sky-800/50',
        ring: 'ring-sky-100 dark:ring-sky-800/50',
        avatar: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
        subtitle: 'text-sky-600 dark:text-sky-400',
        amount: 'text-sky-700 dark:text-sky-300',
    },
} as const;

function ParticipantRow({
    tone,
    name,
    avatarUrl,
    subtitle,
    amount,
}: {
    tone: keyof typeof PARTICIPANT_TONE;
    name: string;
    avatarUrl?: string | null;
    subtitle: string;
    amount: string;
}) {
    const t = PARTICIPANT_TONE[tone];

    return (
        <div
            className={cn(
                'flex items-center gap-3 rounded-lg border px-3 py-2 shadow-sm',
                'bg-gray-50 dark:bg-zinc-800',
                t.shell,
            )}
        >
            {avatarUrl ? (
                <img
                    src={avatarUrl}
                    alt={name}
                    className={cn('h-6 w-6 shrink-0 rounded-full object-cover ring-1', t.ring)}
                />
            ) : (
                <div
                    className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                        t.avatar,
                    )}
                >
                    {name.charAt(0)}
                </div>
            )}
            <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold leading-tight text-zinc-900 dark:text-zinc-50">
                    {name}
                </p>
                <p className={cn('truncate text-[10px] leading-tight', t.subtitle)}>{subtitle}</p>
            </div>
            <span className={cn('shrink-0 text-sm font-bold', t.amount)}>{amount}</span>
        </div>
    );
}

function Metric({
    label,
    value,
    valueClass,
}: {
    label: string;
    value: string;
    valueClass?: string;
}) {
    return (
        <div className="rounded-lg bg-gray-50 px-2.5 py-2 dark:bg-zinc-800/70">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">{label}</p>
            <p className={cn('mt-0.5 break-words text-sm font-semibold text-zinc-900 dark:text-zinc-100', valueClass)}>{value}</p>
        </div>
    );
}

function SummaryCard({
    label,
    value,
    sub,
    accent,
}: {
    label: string;
    value: string;
    sub?: string;
    accent: string;
}) {
    return (
        <div className={cn('rounded-xl border p-3', accent)}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:text-zinc-400">{label}</p>
            <p className="mt-1 break-words text-xl font-bold leading-tight text-gray-900 dark:text-zinc-50">{value}</p>
            {sub && <p className="mt-0.5 text-[10px] text-gray-500 dark:text-zinc-400">{sub}</p>}
        </div>
    );
}
